import { useCallback, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { bucketPins } from '../utils/mapPinCluster';

const pricePinIcon = (priceLabel, isActive) =>
  L.divIcon({
    className: 'srm-pin-root',
    html: `<div class="srm-price-pin ${isActive ? 'srm-price-pin--active' : ''}">${priceLabel}</div>`,
    iconSize: [72, 32],
    iconAnchor: [36, 32],
  });

const clusterIcon = (count) =>
  L.divIcon({
    className: 'srm-cluster-root',
    html: `<div class="srm-cluster-pin"><span>${count}</span><small>stays</small></div>`,
    iconSize: [52, 52],
    iconAnchor: [26, 26],
  });

const HotelMapPinCluster = ({ pins = [], selectedHotelId, onPinClick }) => {
  const map = useMap();
  const layerRef = useRef(null);

  const renderMarkers = useCallback(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    const grouped = bucketPins(pins, map.getZoom());
    const layer = L.layerGroup();

    grouped.forEach((entry) => {
      if (entry.type === 'pin') {
        const pin = entry.pin;
        const isActive = selectedHotelId && String(selectedHotelId) === String(pin.id);
        const marker = L.marker([pin.lat, pin.lng], {
          icon: pricePinIcon(pin.priceLabel, isActive),
          zIndexOffset: isActive ? 1000 : 0,
        });
        marker.on('click', () => onPinClick?.(pin));
        layer.addLayer(marker);
        return;
      }

      const marker = L.marker([entry.lat, entry.lng], {
        icon: clusterIcon(entry.count),
        zIndexOffset: 200,
      });
      marker.on('click', () => {
        map.setView([entry.lat, entry.lng], Math.min(map.getZoom() + 2, 16));
      });
      layer.addLayer(marker);
    });

    layer.addTo(map);
    layerRef.current = layer;
  }, [map, pins, selectedHotelId, onPinClick]);

  useEffect(() => {
    renderMarkers();
    map.on('zoomend', renderMarkers);
    return () => {
      map.off('zoomend', renderMarkers);
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, renderMarkers]);

  return null;
};

export default HotelMapPinCluster;
